import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { type ColumnDef } from "@tanstack/react-table"
import { AlertTriangle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useAlerts, useSites } from "@/lib/hooks/use-sites"
import type { Alert } from "@/types/api"

export const Route = createFileRoute("/_authenticated/alerts")({
  component: AlertsPage,
})

function getColumns(siteNameMap: Map<string, string>): ColumnDef<Alert, unknown>[] {
  return [
  {
    accessorKey: "site",
    header: "Site",
    cell: ({ row }) => siteNameMap.get(row.original.site) ?? row.original.site,
  },
  {
    id: "level",
    header: "Level",
    cell: ({ row }) => {
      const level = row.original.threshold?.level
      const variant =
        level === "red" ? "destructive" : level === "amber" ? "warning" : "success"
      return <Badge variant={variant}>{level ?? "—"}</Badge>
    },
  },
  {
    id: "device",
    header: "Device",
    cell: ({ row }) => (
      <span className="font-medium text-[var(--color-text-primary)]">
        {row.original.measurement?.measurement_name || "—"}
      </span>
    ),
  },
  {
    id: "channel",
    header: "Channel",
    cell: ({ row }) => row.original.threshold?.channel || "—",
  },
  {
    accessorKey: "start_time",
    header: "Start Date",
    cell: ({ row }) =>
      row.original.start_time
        ? new Date(row.original.start_time).toLocaleDateString()
        : "—",
  },
  {
    accessorKey: "end_time",
    header: "End Date",
    cell: ({ row }) =>
      row.original.end_time
        ? new Date(row.original.end_time).toLocaleDateString()
        : "—",
  },
]
}

function AlertsPage() {
  const { data: alerts, isLoading, isError, error } = useAlerts()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const columns = useMemo(() => getColumns(siteNameMap), [siteNameMap])

  return (
    <EntityTablePage
      icon={<AlertTriangle className="h-5 w-5 text-[var(--color-accent-amber)]" />}
      title="Alerts"
      description={`${alerts?.length ?? 0} active alerts`}
      searchPlaceholder="Search alerts..."
      emptyMessage="No active alerts"
      columns={columns}
      data={alerts}
      isLoading={isLoading}
      isError={isError}
      error={error}
      dataTour="alerts-table"
    />
  )
}
